import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";
import AddIcon from "@mui/icons-material/AddRounded";
import BookmarkBorderRoundedIcon from "@mui/icons-material/BookmarkBorderRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import IndeterminateCheckBoxRoundedIcon from "@mui/icons-material/IndeterminateCheckBoxRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import MarkEmailUnreadRoundedIcon from "@mui/icons-material/MarkEmailUnreadRounded";
import NotInterestedRoundedIcon from "@mui/icons-material/NotInterestedRounded";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Popover from "@mui/material/Popover";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { addCommitment, requestInvitation } from "../../redux/eventsSlice";
import Utils from "../../utils/Utils";

const { formatNumber } = Utils;

export default function Commitment({ event, profile, interactionDisabled }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const popoverControl = useRef(null);
  const handlePopoverOpen = () => {
    setAnchorEl(popoverControl.current);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const { commitmentStatus, invitationRequestStatus } = useSelector(
    (state) => state.events
  );
  const dispatch = useDispatch();
  const invitationRequest = event.userSentInvitationRequest || {};
  const [commitment, setCommitment] = useState(
    invitationRequest?.commitment || "interested"
  );
  const handleCommitmentChange = (event, newCommitment) => {
    if (newCommitment !== null) {
      setCommitment(newCommitment);
      if (newCommitment === "request-invitation") {
        handleRequestInvitation();
        return;
      }
      updateCommitment(newCommitment);
    }
    handlePopoverClose();
  };
  const updateCommitment = (commitment) => {
    dispatch(
      addCommitment({
        commitment,
        eventID: event._id,
        userID: profile._id,
      })
    );
  };
  const handleRequestInvitation = () => {
    dispatch(requestInvitation(event._id)).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        if (result.payload && result.payload.eventID) {
          if (result.payload.status === "pending") {
            toast.success(t("invitationRequestSuccess"));
          } else if (result.payload.status === "accepted") {
            handlePopoverOpen();
          }
        }
      }
    });
  };
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography fontSize="medium">
        {event.commitmentsCount.going > 0 &&
          `${formatNumber(event.commitmentsCount.going)} ${t("goingLower")}`}
      </Typography>
      {!interactionDisabled && (
        <>
          {profile._id === event.authorID ? (
            <Button
              color="inherit"
              sx={{
                bgcolor: theme.palette.action.hover,
                px: 1.5,
              }}
              onClick={() => {
                navigate(`/events/manage/${event._id}`);
              }}
            >
              {t("manageEvent")}
            </Button>
          ) : (
            <Box
              ref={popoverControl}
              aria-owns={open ? "commitment-popover" : undefined}
              aria-haspopup="true"
              sx={{ my: 0.5 }}
            >
              {!invitationRequest?.eventID && (
                <Button
                  color="inherit"
                  sx={{
                    bgcolor: theme.palette.action.hover,
                    px: 1.5,
                  }}
                  startIcon={
                    event.isUserInterested ? (
                      <BookmarkRoundedIcon />
                    ) : (
                      <BookmarkBorderRoundedIcon />
                    )
                  }
                  endIcon={<KeyboardArrowDownRoundedIcon />}
                  disabled={invitationRequestStatus === "loading"}
                  onClick={() => {
                    dispatch(
                      addCommitment({
                        commitment: "interested",
                        eventID: event._id,
                        userID: profile._id,
                      })
                    ).then((result) => {
                      if (result.meta.requestStatus === "fulfilled") {
                        handlePopoverOpen();
                      }
                    });
                  }}
                >
                  {t("interested")}
                </Button>
              )}
              {invitationRequest?.status === "pending" && (
                <Button
                  color="inherit"
                  sx={{
                    bgcolor: theme.palette.action.hover,
                    px: 1.5,
                  }}
                  startIcon={<MarkEmailReadRoundedIcon />}
                  endIcon={<KeyboardArrowDownRoundedIcon />}
                  onClick={() => {
                    handlePopoverOpen();
                  }}
                >
                  {t("inviteRequested")}
                </Button>
              )}
              {invitationRequest?.status === "accepted" && (
                <Button
                  color="info"
                  sx={{
                    bgcolor: theme.palette.action.hover,
                    px: 1.5,
                  }}
                  startIcon={<MarkEmailUnreadRoundedIcon />}
                  endIcon={<KeyboardArrowDownRoundedIcon />}
                  onClick={() => {
                    handlePopoverOpen();
                  }}
                >
                  {t("rsvp")}
                </Button>
              )}
              {invitationRequest?.status === "declined" && (
                <Button
                  color="warning"
                  sx={{
                    bgcolor: theme.palette.action.hover,
                    px: 1.5,
                  }}
                  startIcon={<ErrorOutlineRoundedIcon />}
                  endIcon={<KeyboardArrowDownRoundedIcon />}
                  onClick={() => {
                    handlePopoverOpen();
                  }}
                >
                  {t("inviteDeclined")}
                </Button>
              )}
              <Popover
                id="commitment-popover"
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
                sx={{
                  "& .MuiPaper-root": {
                    borderRadius: "0.5rem",
                  },
                }}
              >
                <Box sx={{ p: 2 }}>
                  {invitationRequest?.status === "pending" && (
                    <Typography variant="h6" gutterBottom>
                      {t("inviteRequested")}
                    </Typography>
                  )}
                  {invitationRequest?.status === "accepted" && (
                    <Typography color="success.main" variant="h6" gutterBottom>
                      {t("rsvp")}
                    </Typography>
                  )}
                  {invitationRequest?.status !== "accepted" &&
                    invitationRequest?.status !== "pending" && (
                      <Typography variant="h6" gutterBottom>
                        {t("requestInvitation")}
                      </Typography>
                    )}
                  <FormControl sx={{ width: "100%" }}>
                    <ToggleButtonGroup
                      disabled={commitmentStatus === "loading"}
                      orientation="vertical"
                      value={commitment}
                      exclusive
                      size="small"
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        "& button": {
                          width: "100%",
                        },
                        "& button > div": {
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          textAlign: "left",
                        },
                        "& button > div > svg": {
                          marginRight: "0.25rem",
                        },
                      }}
                      onChange={handleCommitmentChange}
                    >
                      {invitationRequest?.status !== "accepted" &&
                        invitationRequest?.status !== "pending" &&
                        invitationRequest?.status !== "declined" && (
                          <ToggleButton
                            value="request-invitation"
                            aria-label={t("requestInvitation")}
                          >
                            <Box sx={{ color: theme.palette.info.main }}>
                              <AddIcon />
                              {t("requestInvitation")}
                            </Box>
                          </ToggleButton>
                        )}
                      {invitationRequest?.status === "accepted" && (
                        <ToggleButton value="going" aria-label={t("going")}>
                          <Box sx={{ color: theme.palette.success.main }}>
                            <AddBoxRoundedIcon />
                            {t("going")}
                          </Box>
                        </ToggleButton>
                      )}
                      {invitationRequest?.status === "accepted" && (
                        <ToggleButton
                          value="not-going"
                          aria-label={t("notGoing")}
                        >
                          <Box sx={{ color: theme.palette.error.main }}>
                            <IndeterminateCheckBoxRoundedIcon />
                            {t("notGoing")}
                          </Box>
                        </ToggleButton>
                      )}
                      {invitationRequest?.status === "pending" && (
                        <ToggleButton
                          value="not-going"
                          aria-label={t("withdrawRequest")}
                        >
                          <Box sx={{ color: theme.palette.error.main }}>
                            <NotInterestedRoundedIcon />
                            {t("withdrawRequest")}
                          </Box>
                        </ToggleButton>
                      )}
                      {invitationRequest?.status !== "accepted" &&
                        invitationRequest?.status !== "pending" && (
                          <ToggleButton
                            value="remove"
                            aria-label={t("remove")}
                          >
                            <Box sx={{ color: theme.palette.error.main }}>
                              <NotInterestedRoundedIcon />
                              {t("remove")}
                            </Box>
                          </ToggleButton>
                        )}
                    </ToggleButtonGroup>
                  </FormControl>
                </Box>
              </Popover>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
